import type {
  ModelWithFilesAndLinks,
  ModelFile,
  ModelLink,
  ModelQuery,
  CreateModelInput,
  UpdateModelInput,
  AddModelFileInput,
  AddModelLinkInput,
  PaginationMeta,
  ExternalModelMetadata,
  PrinterWithRelations,
  PrinterLibraryQuery,
  FilePlatesResponse,
} from '@maker-toolchest/shared';
import { api } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';

// Paginated result interface
export interface PaginatedResult<T> {
  items: T[];
  pagination: PaginationMeta;
}

// Helper to build query strings
function buildQueryString(params?: Record<string, any>): string {
  if (!params) return '';
  const entries = Object.entries(params).filter(([_, v]) => v != null);
  if (entries.length === 0) return '';
  const searchParams = new URLSearchParams();
  entries.forEach(([k, v]) => {
    // Handle arrays by appending each item separately
    if (Array.isArray(v)) {
      v.forEach((item) => searchParams.append(k, String(item)));
    } else {
      searchParams.append(k, String(v));
    }
  });
  return `?${searchParams.toString()}`;
}

// ============================================================================
// MODELS API
// ============================================================================

export const modelsApi = {
  /**
   * Create a new model (name and description only)
   */
  create: async (input: CreateModelInput): Promise<ModelWithFilesAndLinks> => {
    const response = await api.post<ModelWithFilesAndLinks>('/models', input);
    if (!response.success || !response.data) {
      throw new Error('Failed to create model');
    }
    return response.data;
  },

  /**
   * List tenant's models
   */
  list: async (query?: ModelQuery): Promise<PaginatedResult<ModelWithFilesAndLinks>> => {
    const response = await api.get<ModelWithFilesAndLinks[]>(`/models${buildQueryString(query)}`);
    if (!response.success || !response.data || !response.meta?.pagination) {
      throw new Error('Failed to fetch models');
    }
    return {
      items: response.data,
      pagination: response.meta.pagination,
    };
  },

  /**
   * Get a single model with files and links
   */
  getById: async (id: string): Promise<ModelWithFilesAndLinks> => {
    const response = await api.get<ModelWithFilesAndLinks>(`/models/${id}`);
    if (!response.success || !response.data) {
      throw new Error('Failed to fetch model');
    }
    return response.data;
  },

  /**
   * Update a model
   */
  update: async (id: string, input: UpdateModelInput): Promise<ModelWithFilesAndLinks> => {
    const response = await api.patch<ModelWithFilesAndLinks>(`/models/${id}`, input);
    if (!response.success || !response.data) {
      throw new Error('Failed to update model');
    }
    return response.data;
  },

  /**
   * Delete a model (cascades to files and links)
   */
  delete: async (id: string): Promise<void> => {
    const response = await api.delete<void>(`/models/${id}`);
    if (!response.success) {
      throw new Error('Failed to delete model');
    }
  },

  /**
   * Preview link metadata without creating the link
   */
  previewLinkMetadata: async (url: string): Promise<ExternalModelMetadata> => {
    const response = await api.post<ExternalModelMetadata>('/models/preview-link', { url });
    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to extract metadata from URL');
    }
    return response.data;
  },

  /**
   * Get unique link domains for filtering
   */
  getLinkDomains: async (): Promise<string[]> => {
    const response = await api.get<string[]>('/models/link-domains');
    if (!response.success || !response.data) {
      throw new Error('Failed to fetch link domains');
    }
    return response.data;
  },

  // ============================================================================
  // FILE OPERATIONS
  // ============================================================================

  /**
   * Add STL file to model
   */
  addFile: async (
    modelId: string,
    file: File,
    input: AddModelFileInput
  ): Promise<ModelFile> => {
    const formData = new FormData();
    formData.append('file', file);
    if (input.notes) formData.append('notes', input.notes);

    const response = await api.postFormData<ModelFile>(`/models/${modelId}/files`, formData);
    if (!response.success || !response.data) {
      throw new Error('Failed to add file to model');
    }
    return response.data;
  },

  /**
   * Delete file from model
   */
  deleteFile: async (modelId: string, fileId: string): Promise<void> => {
    const response = await api.delete<void>(`/models/${modelId}/files/${fileId}`);
    if (!response.success) {
      throw new Error('Failed to delete file');
    }
  },

  /**
   * Get signed download URL for a file
   */
  getFileDownloadUrl: async (modelId: string, fileId: string): Promise<{ url: string; expiresIn: number }> => {
    const response = await api.get<{ url: string; expiresIn: number }>(`/models/${modelId}/files/${fileId}/download-url`);
    if (!response.success || !response.data) {
      throw new Error('Failed to get download URL');
    }
    return response.data;
  },

  /**
   * Get signed URLs for all images associated with a file (thumbnails and plate images)
   */
  getFileImageUrls: async (
    modelId: string,
    fileId: string
  ): Promise<{
    thumbnails: {
      thumbnail3mf: string | null;
      thumbnailMiddle: string | null;
      thumbnailSmall: string | null;
    };
    plates: Array<{
      plateNumber: number;
      thumbnail: string | null;
      topView: string | null;
      pick: string | null;
    }>;
    expiresIn: number;
  }> => {
    const response = await api.get<{
      thumbnails: {
        thumbnail3mf: string | null;
        thumbnailMiddle: string | null;
        thumbnailSmall: string | null;
      };
      plates: Array<{
        plateNumber: number;
        thumbnail: string | null;
        topView: string | null;
        pick: string | null;
      }>;
      expiresIn: number;
    }>(`/models/${modelId}/files/${fileId}/image-urls`);
    if (!response.success || !response.data) {
      throw new Error('Failed to get image URLs');
    }
    return response.data;
  },

  // ============================================================================
  // LINK OPERATIONS
  // ============================================================================

  /**
   * Add external link to model (auto-detects provider)
   */
  addLink: async (modelId: string, input: AddModelLinkInput): Promise<ModelLink> => {
    const response = await api.post<ModelLink>(`/models/${modelId}/links`, input);
    if (!response.success || !response.data) {
      throw new Error('Failed to add link to model');
    }
    return response.data;
  },

  /**
   * Retry metadata extraction for a link
   */
  retryLinkMetadata: async (modelId: string, linkId: string): Promise<void> => {
    const response = await api.post<void>(`/models/${modelId}/links/${linkId}/retry-metadata`, {});
    if (!response.success) {
      throw new Error('Failed to retry metadata extraction');
    }
  },

  /**
   * Delete link from model
   */
  deleteLink: async (modelId: string, linkId: string): Promise<void> => {
    const response = await api.delete<void>(`/models/${modelId}/links/${linkId}`);
    if (!response.success) {
      throw new Error('Failed to delete link');
    }
  },

  // ============================================================================
  // BUILD PLATE OPERATIONS
  // ============================================================================

  /**
   * Get all build plates for a model file
   */
  getFilePlates: async (modelId: string, fileId: string): Promise<FilePlatesResponse> => {
    const response = await api.get<FilePlatesResponse>(`/models/${modelId}/files/${fileId}/plates`);
    if (!response.success || !response.data) {
      throw new Error('Failed to fetch build plates');
    }
    return response.data;
  },

  /**
   * Reprocess metadata extraction for a file (admin only, dev mode)
   */
  reprocessFile: async (fileId: string): Promise<void> => {
    const response = await api.post<void>(`/admin/reprocess-file/${fileId}`, {});
    if (!response.success) {
      throw new Error('Failed to queue file reprocessing');
    }
  },
};

// ============================================================================
// PRINTER LIBRARY API (for viewer printer selection)
// ============================================================================

export const printerLibraryApi = {
  /**
   * Search printer catalog (for model viewer)
   */
  search: async (query?: PrinterLibraryQuery): Promise<PaginatedResult<PrinterWithRelations>> => {
    const response = await api.get<PrinterWithRelations[]>(`/printers/library${buildQueryString(query)}`);
    if (!response.success || !response.data || !response.meta?.pagination) {
      throw new Error('Failed to fetch printer library');
    }
    return {
      items: response.data,
      pagination: response.meta.pagination,
    };
  },
};

// ============================================================================
// REACT QUERY HOOKS
// ============================================================================

/**
 * Hook to search printer library
 * Used by model viewer for printer selection
 */
export function useListPrinters(search?: string, technology?: 'FDM' | 'SLA') {
  return useQuery({
    queryKey: ['printers', 'library', { search, technology }],
    queryFn: () => printerLibraryApi.search({
      page: 1,
      limit: 100,
      sortBy: 'name',
      sortOrder: 'asc',
      includeSystem: true,
      includeCustom: true,
      search,
      technology,
    }),
  });
}

/**
 * Hook to fetch build plates for a model file
 * Returns extracted metadata including objects and filament sequences
 */
export function useFilePlates(modelId: string, fileId: string, enabled = true) {
  return useQuery({
    queryKey: ['models', modelId, 'files', fileId, 'plates'],
    queryFn: () => modelsApi.getFilePlates(modelId, fileId),
    enabled,
  });
}

// Re-export types for convenience
export type { ModelQuery };
